import { type CsvField, type CsvFieldType, type CsvSchema } from './schema';

/**
 * An import's columns as the server defines them (the ImportSchema GraphQL type, which
 * satisfies this). The server validates the rows, so it owns what a column is.
 */
export type ServerImportSchema = {
  columns: ServerImportColumn[];
  keyColumns: string[];
  tieBreaker?: string | null;
};

export type ServerImportColumn = {
  key: string;
  label: string;
  type: string;
  required: boolean;
  multiple: boolean;
  compareLiterally: boolean;
  fixedOnceAdded: boolean;
  addOnly: boolean;
  enumValues?: string[] | null;
  optionsFrom?: string | null;
};

/**
 * What the page adds to a server column: how it looks and helps, never what it is. It
 * may require a column the server leaves optional, but not the reverse.
 */
export type ColumnBehaviour = Pick<
  CsvField,
  | 'allow'
  | 'example'
  | 'extensionField'
  | 'lockedFor'
  | 'required'
  | 'requiredFor'
  | 'sensitive'
  | 'shortLabel'
  | 'validate'
  | 'width'
>;

const FIELD_TYPES: Record<string, CsvFieldType> = {
  STRING: 'string',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  DATE: 'date',
  ENUM: 'enum',
  PHONE: 'phone',
  EMAIL: 'email'
};

/**
 * The CSV schema for `server`'s columns. `behaviours` adds page-only behaviour by column
 * key, and `options` supplies the allowed values of columns whose values the page holds,
 * by the column's `optionsFrom`. Naming a column the server does not define throws, so
 * the page cannot drift from the server unnoticed.
 */
export const csvSchemaFromServer = (
  server: ServerImportSchema,
  {
    behaviours = {},
    options = {}
  }: { behaviours?: Record<string, ColumnBehaviour>; options?: Record<string, string[]> } = {}
): CsvSchema => {
  const keys = new Set(server.columns.map((column) => column.key));
  for (const key of Object.keys(behaviours)) {
    if (!keys.has(key)) {
      throw new Error(`The server's import schema has no "${key}" column`);
    }
  }
  return {
    keyColumn: server.keyColumns,
    tieBreaker: server.tieBreaker ?? undefined,
    fields: server.columns.map((column) => {
      const type = FIELD_TYPES[column.type];
      if (!type) {
        throw new Error(`Import column "${column.key}" has an unknown type ${column.type}`);
      }
      const enumValues =
        column.enumValues ?? (column.optionsFrom ? options[column.optionsFrom] : undefined);
      if (type === 'enum' && !enumValues) {
        throw new Error(`Import column "${column.key}" needs its options supplied`);
      }
      const behaviour = behaviours[column.key];
      return {
        key: column.key,
        label: column.label,
        type,
        // A phone cell holds one number.
        multiple: type !== 'phone' && column.multiple,
        compareLiterally: column.compareLiterally,
        fixedOnceAdded: column.fixedOnceAdded,
        addOnly: column.addOnly,
        enumValues: type === 'enum' ? enumValues : undefined,
        ...behaviour,
        required: column.required || behaviour?.required === true
      };
    })
  };
};
